import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { apiError } from "@/lib/apiError";
import { Loader2, CreditCard } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { IDR, USD, dateTime } from "@/lib/format";
import { AppHeader } from "@/components/AppHeader";
import { PayPalCheckout } from "@/components/PayPalCheckout";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { defaultPaymentMethod } from "@/lib/paymentRegion";

/** Translates a known order status, or shows the raw value for one we have not met yet. */
const orderStatusLabel = (t, status) => {
  const label = t(`order_status_${status}`);
  return label.startsWith("order_status_") ? status : label;
};
import { SupportButton } from "@/components/SupportButton";
import { PlanPrice } from "@/components/PlanPrice";
import { EarlyAccessBadge } from "@/components/EarlyAccessBadge";
import { btnPrimary, btnGhost } from "@/components/Brand";
import { inputClass, labelClass } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { logError } from "@/lib/log";

const loadSnap = (clientKey, isProduction) =>
  new Promise((resolve, reject) => {
    if (window.snap) return resolve(window.snap);
    const script = document.createElement("script");
    script.src = isProduction
      ? "https://app.midtrans.com/snap/snap.js"
      : "https://app.sandbox.midtrans.com/snap/snap.js";
    script.setAttribute("data-client-key", clientKey);
    script.onload = () => resolve(window.snap);
    script.onerror = reject;
    document.body.appendChild(script);
  });

export default function Billing() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isOwner = user?.tenant_role === "owner";
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [config, setConfig] = useState(null);
  const [earlyAccess, setEarlyAccess] = useState(null);
  const [offer, setOffer] = useState(null);
  const [promo, setPromo] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [method, setMethod] = useState(defaultPaymentMethod);
  const [busy, setBusy] = useState("");

  const applyPromo = async () => {
    const code = promo.trim();
    if (!code) {
      setPromoInfo(null);
      return;
    }
    try {
      const res = await api.get("/billing/promo", { params: { code } });
      setPromoInfo(res.data);
      toast.success(t("promo_applied", { code: res.data.code, pct: res.data.discount_percent }));
    } catch (err) {
      setPromoInfo(null);
      toast.error(apiError(err, "Kode promo tidak valid"));
    }
  };

  const load = useCallback(async () => {
    const [p, s, c, pub] = await Promise.all([
      api.get("/billing/plans"),
      api.get("/billing/subscription"),
      api.get("/billing/config"),
      api.get("/config"),
    ]);
    setPlans(p.data);
    setSub(s.data);
    setConfig(c.data);
    setEarlyAccess(pub.data.early_access);
    if (user?.tenant_role !== "owner") return;
    try {
      const o = await api.get("/billing/offer");
      setOffer(o.data?.active ? o.data : null);
    } catch (err) {
      logError("Could not load offer", err);
    }
  }, [user?.tenant_role]);

  // Ticking countdown for the offer banner.
  const [offerLeft, setOfferLeft] = useState(0);
  useEffect(() => {
    setOfferLeft(offer?.seconds_left || 0);
    if (!offer?.active) return;
    const id = setInterval(() => setOfferLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [offer]);

  useEffect(() => {
    load().catch((err) => logError("Could not load billing", err));
  }, [load]);

  const pay = async (planId) => {
    setBusy(planId);
    try {
      const res = await api.post("/billing/checkout", { plan_id: planId, promo_code: promo || null });
      const snap = await loadSnap(res.data.client_key, res.data.is_production);
      if (snap && res.data.token) {
        snap.pay(res.data.token, {
          onSuccess: () => { toast.success("Payment received"); load(); },
          onPending: () => toast.info("Payment pending — finish the steps in your app"),
          onError: () => toast.error("Payment failed"),
          onClose: () => toast.info("Checkout closed"),
        });
      } else if (res.data.redirect_url) {
        window.location.href = res.data.redirect_url;
      }
    } catch (err) {
      toast.error(apiError(err, "Could not start checkout"));
    } finally {
      setBusy("");
    }
  };

  if (!sub || !config)
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#707070]">
        <Loader2 className="animate-spin" />
      </div>
    );

  const s = sub.subscription || {};
  const isLifetime = s.status === "lifetime";

  const page = (
    <div className="min-h-screen bg-[#050505] text-[#F3F3F1] grain" data-testid="billing-page">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-5 lg:px-12 py-12 lg:py-16 space-y-12">
        <div>
          <p className="eyebrow mb-3">{t("billing")}</p>
          <h1 className="text-4xl sm:text-5xl tracking-tighter font-medium">{t("plans")}</h1>
        </div>

        <div className="border border-[#D4AF37]/30 bg-[#0F0F0F] p-7" data-testid="current-subscription">
          <p className="eyebrow mb-4">{t("current_plan")}</p>
          <p className="font-mono-accent text-lg text-[#D4AF37]" data-testid="subscription-status">
            {s.plan_name || t(`sub_status_${s.status || "trialing"}`)}
          </p>
          <p className="mt-2 text-xs text-[#707070]">
            {isLifetime
              ? t("lifetime_access")
              : `${s.status === "active" ? t("renews_on") : t("trial_until")}: ${dateTime(s.current_period_end)}`}
          </p>
          {sub.trial?.expired && (
            <div className="mt-5 border-t border-[#2A2A2A] pt-5" data-testid="trial-expired-notice">
              <p className="text-sm text-[#F3F3F1]">{t("trial_locked_title")}</p>
              <p className="mt-1.5 text-xs text-[#A8A8A8]">{t("trial_locked_body")}</p>
            </div>
          )}
        </div>

        {offer && (
          <div className="border border-[#D4AF37] bg-[#D4AF37]/5 p-7 flex flex-wrap items-center justify-between gap-5"
            data-testid="billing-oto-banner">
            <div>
              <p className="eyebrow text-[#D4AF37]">{t("oto_eyebrow")}</p>
              <p className="mt-2 text-sm text-[#F3F3F1] max-w-xl leading-relaxed">
                {t("oto_body", { months: offer.bonus_months })}
              </p>
            </div>
            <p className="font-mono-accent text-2xl text-[#D4AF37]" data-testid="billing-oto-countdown">
              {String(Math.floor(offerLeft / 3600)).padStart(2, "0")}:
              {String(Math.floor((offerLeft % 3600) / 60)).padStart(2, "0")}:
              {String(offerLeft % 60).padStart(2, "0")}
            </p>
          </div>
        )}

        {isOwner && !isLifetime && (
          <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-6 flex flex-wrap items-end gap-4" data-testid="promo-block">
            <div className="flex-1 min-w-[220px]">
              <label className={labelClass}>{t("have_promo_code")}</label>
              <input data-testid="promo-code-input" value={promo} maxLength={12}
                onChange={(e) => { setPromo(e.target.value.toUpperCase()); setPromoInfo(null); }}
                onBlur={() => applyPromo()} placeholder="XUA76A"
                className={inputClass} />
            </div>
            <button data-testid="promo-apply-btn" type="button" onClick={applyPromo} className={btnGhost}>
              {t("apply_code")}
            </button>
            {promoInfo ? (
              <p className="text-[11px] text-[#D4AF37] max-w-sm leading-relaxed" data-testid="promo-applied">
                {t("promo_applied", { code: promoInfo.code, pct: promoInfo.discount_percent })}
              </p>
            ) : (
              <p className="text-[11px] text-[#707070] max-w-sm leading-relaxed" data-testid="promo-note">
                {t("promo_note")}
              </p>
            )}
          </div>
        )}

        {isOwner && !isLifetime && config.paypal_enabled && (
          <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-6" data-testid="payment-method-block">
            <p className="eyebrow mb-4">{t("pay_with")}</p>
            <div className="flex flex-wrap gap-3">
              {[
                { id: "midtrans", label: t("pay_local") },
                { id: "paypal", label: t("pay_paypal") },
              ].map((m) => (
                <button key={m.id} type="button" data-testid={`method-${m.id}`}
                  onClick={() => setMethod(m.id)}
                  className={`font-mono-accent text-[10px] tracking-[0.18em] uppercase px-5 py-3 border transition-colors duration-300 ${
                    method === m.id
                      ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                      : "border-[#2A2A2A] text-[#707070] hover:text-[#A8A8A8]"
                  }`}>
                  {m.label}
                </button>
              ))}
            </div>
            {method === "paypal" && (
              <p className="mt-4 text-[11px] text-[#707070] max-w-lg leading-relaxed" data-testid="paypal-note">
                {t("paypal_note")}
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="plans-grid">
          {plans.map((p) => {
            const annual = (p.interval_months || 1) >= 12;
            const monthly = plans.find((x) => (x.interval_months || 1) === 1 && x.price_idr === Math.max(...plans.filter((y) => (y.interval_months || 1) === 1).map((y) => y.price_idr)));
            const savings = annual && monthly ? monthly.price_idr * 12 - p.price_idr : 0;
            const highlight = annual || p.best_value;
            return (
              <div key={p.plan_id} data-testid={`plan-card-${p.plan_id}`}
                className={`relative border bg-[#0F0F0F] p-8 flex flex-col transition-colors duration-500 ${
                  highlight ? "border-[#D4AF37] shadow-[0_0_60px_rgba(212,175,55,0.08)]" : "border-[#2A2A2A]"
                }`}>
                {highlight && (
                  <span data-testid={`best-value-badge-${p.plan_id}`}
                    className="absolute -top-3 left-8 bg-[#D4AF37] text-black font-mono-accent text-[9px] tracking-[0.2em] uppercase px-3 py-1.5">
                    {t("best_value")}
                  </span>
                )}
                <h3 className="text-2xl tracking-tight">{p.name}</h3>
                <div className="mt-4">
                  <PlanPrice plan={p} testId={`billing-price-${p.plan_id}`} />
                  <EarlyAccessBadge earlyAccess={earlyAccess} testId={`billing-slots-${p.plan_id}`} />
                </div>
                {annual && (
                  <p className="mt-2 font-mono-accent text-[10px] tracking-[0.14em] uppercase text-[#D4AF37]"
                    data-testid={`plan-savings-${p.plan_id}`}>
                    {t("per_month_equiv", { price: IDR(Math.round(p.price_idr / (p.interval_months || 12))) })}
                    {savings > 0 && ` · ${t("save_amount", { amount: IDR(savings) })}`}
                  </p>
                )}
                {promoInfo && (
                  <p className="mt-2 text-xs text-[#D4AF37]" data-testid={`plan-promo-price-${p.plan_id}`}>
                    {t("promo_price_after", {
                      price: IDR(Math.max(1000, Math.round(p.price_idr * (100 - promoInfo.discount_percent) / 100))),
                      was: IDR(p.price_idr),
                    })}
                  </p>
                )}
                {offer && offer.plan_id === p.plan_id && (
                  <p className="mt-2 text-xs text-[#D4AF37]" data-testid={`plan-bonus-${p.plan_id}`}>
                    +{offer.bonus_months} {t("bonus_months_free")}
                  </p>
                )}
                <ul className="mt-6 space-y-2 flex-1">
                  {((lang === "en" && p.features_en?.length ? p.features_en : p.features) || []).map((f) => (
                    <li key={f} className="text-sm text-[#A8A8A8] flex gap-2"><span className="text-[#D4AF37]">·</span> {f}</li>
                  ))}
                </ul>
                {isLifetime ? (
                  <span data-testid={`lifetime-badge-${p.plan_id}`}
                    className="mt-8 text-center border border-[#D4AF37]/40 text-[#D4AF37] py-3.5 text-[10px] tracking-[0.18em] uppercase">
                    {t("lifetime_access")}
                  </span>
                ) : !isOwner ? (
                  <span data-testid={`owner-only-note-${p.plan_id}`}
                    className="mt-8 text-center border border-[#2A2A2A] text-[#707070] py-3.5 text-[10px] tracking-[0.18em] uppercase">
                    {t("owner_only")}
                  </span>
                ) : method === "paypal" ? (
                  (p.price_usd || 0) > 0 ? (
                    <>
                      <p className="mt-6 font-mono-accent text-sm text-[#D4AF37]" data-testid={`plan-usd-price-${p.plan_id}`}>
                        {USD(promoInfo
                          ? Math.max(1, p.price_usd * (100 - promoInfo.discount_percent) / 100)
                          : p.price_usd)}
                      </p>
                      <PayPalCheckout planId={p.plan_id}
                        promoCode={promoInfo ? promoInfo.code : null} onPaid={load} />
                    </>
                  ) : (
                    <span data-testid={`paypal-unavailable-${p.plan_id}`}
                      className="mt-8 text-center border border-[#2A2A2A] text-[#707070] py-3.5 text-[10px] tracking-[0.18em] uppercase">
                      {t("paypal_no_usd_price")}
                    </span>
                  )
                ) : (
                  <button data-testid={`pay-plan-${p.plan_id}`} onClick={() => pay(p.plan_id)} disabled={busy === p.plan_id}
                    className={`${highlight ? btnPrimary : btnGhost} mt-8`}>
                    {busy === p.plan_id ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} {t("pay_now")}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-[#707070]">
          QRIS · Virtual Account · GoPay · ShopeePay · {t("bank_transfer")}
          {config.paypal_enabled && " · PayPal (USD)"}
        </p>

        <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-7" data-testid="payment-history">
          <p className="eyebrow mb-5">{t("payment_history")}</p>
          {sub.orders.length === 0 && <p className="text-sm text-[#A8A8A8]">—</p>}
          {sub.orders.map((o) => (
            <div key={o.order_id} className="flex flex-wrap items-center justify-between gap-3 py-3 border-b border-[#1E1E1E]">
              <span className="font-mono-accent text-[11px] text-[#707070]">{o.order_id}</span>
              <span className="text-sm">{o.plan_name}</span>
              <span className="font-mono-accent text-xs">
                {o.provider === "paypal" && o.amount_usd ? USD(o.amount_usd) : IDR(o.amount)}
              </span>
              {o.provider === "paypal" && (
                <span className="font-mono-accent text-[10px] tracking-[0.18em] uppercase text-[#707070]"
                  data-testid={`order-provider-${o.order_id}`}>PayPal</span>
              )}
              <span data-testid={`order-status-${o.order_id}`}
                className={`font-mono-accent text-[10px] tracking-[0.18em] uppercase ${
                  o.status === "paid" ? "text-[#D4AF37]"
                    : o.status === "pending_review" ? "text-[#C88A2E]" : "text-[#A8A8A8]"}`}>
                {orderStatusLabel(t, o.status)}
              </span>
            </div>
          ))}
          {sub.orders.some((o) => o.status === "pending_review") && (
            <p className="mt-5 text-[11px] text-[#C88A2E] leading-relaxed max-w-2xl" data-testid="pending-review-hint">
              {t("paypal_pending_review_hint")}
            </p>
          )}
        </div>
      </main>
      <SupportButton />
    </div>
  );

  // The SDK script is loaded once, and only for studios who actually chose PayPal.
  if (method !== "paypal" || !config.paypal_enabled) return page;
  return (
    <PayPalScriptProvider options={{ clientId: config.paypal_client_id, currency: "USD", intent: "capture" }}>
      {page}
    </PayPalScriptProvider>
  );
}
